import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedGamesCarousel = ({ featuredGames, onWhatsAppClick, onPreviewClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || featuredGames?.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredGames?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, featuredGames?.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredGames?.length) % featuredGames?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredGames?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  if (!featuredGames || featuredGames?.length === 0) return null;

  const currentGame = featuredGames?.[currentSlide];

  return (
    <div className="relative bg-card rounded-card border border-border shadow-royal overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-royal rounded-full flex items-center justify-center">
            <Icon name="Star" size={16} color="white" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-text-primary">
              Featured Games
            </h2>
            <p className="text-sm text-text-secondary">
              Handpicked premium gaming experiences
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="p-2 rounded-button hover:bg-muted transition-royal"
            title={isAutoPlaying ? 'Pause autoplay' : 'Resume autoplay'}
          >
            <Icon name={isAutoPlaying ? "Pause" : "Play"} size={16} />
          </button>
        </div>
      </div>
      {/* Carousel Content */}
      <div className="relative h-96 lg:h-80">
        {/* Game Background */}
        <div className="absolute inset-0">
          <Image
            src={currentGame?.featuredImage || currentGame?.image}
            alt={currentGame?.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
        </div>

        {/* Game Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-6 lg:px-12">
            <div className="max-w-2xl">
              {/* Game Category */}
              <div className="flex items-center space-x-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-button bg-primary/20 text-primary text-sm font-medium">
                  <Icon name="Crown" size={14} className="mr-1" />
                  {currentGame?.category}
                </span>
                {currentGame?.isLive && (
                  <span className="inline-flex items-center px-3 py-1 rounded-button bg-error/20 text-error text-sm font-medium">
                    <div className="w-2 h-2 bg-error rounded-full animate-pulse mr-1"></div>
                    LIVE
                  </span>
                )}
              </div>

              {/* Game Title */}
              <h3 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-3">
                {currentGame?.name}
              </h3>

              {/* Game Description */}
              <p className="text-white/90 text-lg mb-6 leading-relaxed">
                {currentGame?.featuredDescription || currentGame?.description}
              </p>

              {/* Game Stats */}
              <div className="flex items-center space-x-6 mb-6">
                <div className="text-center">
                  <p className="text-white/70 text-sm">Min Bet</p>
                  <p className="text-white font-bold text-lg">
                    {formatAmount(currentGame?.minBet)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-white/70 text-sm">Max Win</p>
                  <p className="text-white font-bold text-lg">
                    {formatAmount(currentGame?.maxWin)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-white/70 text-sm">Players</p>
                  <p className="text-white font-bold text-lg">
                    {currentGame?.playerCount || '1,247'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-white/70 text-sm">Rating</p>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} color="gold" fill="gold" />
                    <p className="text-white font-bold text-lg">
                      {currentGame?.rating}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <Button
                  variant="default"
                  size="lg"
                  onClick={() => onWhatsAppClick(currentGame)}
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-gradient-royal hover:shadow-glow"
                >
                  Play via WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => onPreviewClick(currentGame)}
                  iconName="Play"
                  iconPosition="left"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Watch Preview
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        {featuredGames?.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-royal"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-royal"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </>
        )}
      </div>
      {/* Slide Indicators */}
      {featuredGames?.length > 1 && (
        <div className="flex items-center justify-center space-x-2 p-4 bg-muted/50">
          {featuredGames?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-royal ${
                index === currentSlide 
                  ? 'bg-primary w-8' :'bg-text-muted hover:bg-text-secondary'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FeaturedGamesCarousel;